import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { History, X, Play, Trash2, Clock } from 'lucide-react'
import { useRequestHistoryStore, useAPIRequestStore } from '../../stores/apiStore'

const RequestHistoryFAB: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { history, removeFromHistory, clearHistory } = useRequestHistoryStore()
  const { 
    setSelectedEndpoint, 
    setBaseUrl, 
    setHeaders, 
    setRequestBody, 
    setQueryParams 
  } = useAPIRequestStore()

  const handleRequestSelect = (request: any) => {
    // Load the request details into the current request store
    if (request.endpoint) setSelectedEndpoint(request.endpoint)
    if (request.baseUrl) setBaseUrl(request.baseUrl)
    if (request.headers) setHeaders(request.headers)
    if (request.requestBody) setRequestBody(request.requestBody)
    if (request.queryParams) setQueryParams(request.queryParams)
    setIsOpen(false)
  }

  const hasHistory = history.length > 0

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-20 z-40"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* History Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed bottom-20 right-4 w-96 max-h-96 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <History className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Request History
                </h3>
                {hasHistory && (
                  <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full">
                    {history.length}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {hasHistory && (
                  <button
                    onClick={clearHistory}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title="Clear all history"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="max-h-80 overflow-y-auto">
              {!hasHistory ? (
                <div className="p-6 text-center">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    No request history yet. Send some requests to see them here.
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {history.map((request) => (
                    <motion.div
                      key={request.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="group flex items-center justify-between p-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                      onClick={() => handleRequestSelect(request)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span
                            className={`
                              text-xs font-mono px-2 py-1 rounded
                              ${request.endpoint?.method === 'GET' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 
                                request.endpoint?.method === 'POST' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' : 
                                request.endpoint?.method === 'PUT' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                                request.endpoint?.method === 'DELETE' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}
                            `}
                          >
                            {request.endpoint?.method || 'N/A'}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {request.endpoint?.path || 'Unknown endpoint'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="w-3 h-3" />
                          <span>{new Date(request.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRequestSelect(request)
                          }}
                          className="p-1 text-green-600 hover:text-green-800 transition-colors"
                          title="Load request"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            removeFromHistory(request.id)
                          }}
                          className="p-1 text-red-500 hover:text-red-700 transition-colors"
                          title="Remove from history"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: 0.5 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          fixed bottom-4 right-4 z-50
          w-14 h-14 bg-primary-600 hover:bg-primary-700 
          text-white rounded-full shadow-lg hover:shadow-xl
          flex items-center justify-center
          transition-all duration-200
          ${isOpen ? 'rotate-45' : 'rotate-0'}
        `}
        title="Request History"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <div className="relative">
            <History className="w-6 h-6" />
            {hasHistory && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {history.length > 9 ? '9+' : history.length}
              </span>
            )}
          </div>
        )}
      </motion.button>
    </>
  )
}

export default RequestHistoryFAB
